import { isEmpty } from 'lodash';

import { Button } from '@/components/button';
import { Icon } from '@/components/icon';
import { Link } from '@/components/link';
import { getAdvancedSearchSummaryLabel } from '@/components/modules/advanced-search/utils';
import { Text } from '@/components/text';
import { AIRPORT, ROUTING_SEARCH_URL, SEAPORT } from '@/lib/constants';
import { RouteApiBodyParams, RouteSearchUsageEvent } from '@/types';
import { getCargoOptionSelectionString } from '@/utils/cargo';
import { formatTime } from '@/utils/format';
import { getPreferredIdentifier } from '@/utils/helpers';
import { QUERY_PARAM } from '@/utils/helpers/api-urls/constants';
import { tv } from '@/utils/styles';
import { trackUser } from '@/utils/tracking';

export interface SearchHistoryRowProps {
  event: RouteSearchUsageEvent;
  variant?: 'mini' | 'default';
}

// Required to handle possibility of legacy event data that contained an array of location objects
const getEventSearchQuery = (
  event: RouteSearchUsageEvent,
): { origin: string; destination: string; advancedSearch?: string; cargo?: string } | null => {
  if (!event.data) return null;

  if ('origin' in event.data && 'destination' in event.data) {
    const { origin, destination, params, cargo } = event.data as RouteApiBodyParams;
    const baseQuery = {
      [QUERY_PARAM.ORIGIN]: origin.name,
      [QUERY_PARAM.DESTINATION]: destination.name,
    };

    return {
      ...baseQuery,
      ...(!isEmpty(params) ? { [QUERY_PARAM.ADVANCED_SEARCH]: JSON.stringify(params) } : {}),
      ...(!isEmpty(cargo) ? { [QUERY_PARAM.CARGO]: JSON.stringify(cargo) } : {}),
    };
  }

  return null;
};

// Required to handle possibility of legacy event data that contained an array of location objects
const getEventLocationNames = (
  event: RouteSearchUsageEvent,
): { from: string; to: string; isPortPair: boolean } | null => {
  if (!event.data) return null;

  if ('origin' in event.data && 'destination' in event.data) {
    const { origin, destination } = event.data as RouteApiBodyParams;

    const originIsPort = origin?.type === AIRPORT || origin?.type === SEAPORT;
    const destinationIsPort = destination.type === AIRPORT || destination.type === SEAPORT;
    const originIdentifier = origin.identifier ? getPreferredIdentifier([origin.identifier]) : null;
    const destinationIdentifier = destination.identifier ? getPreferredIdentifier([destination.identifier]) : null;

    return {
      from: (originIsPort ? originIdentifier?.value : origin.shortName) || '',
      to: (destinationIsPort ? destinationIdentifier?.value : destination.shortName) || '',
      isPortPair: originIsPort && destinationIsPort,
    };
  }

  return null;
};

const SearchHistoryRow = ({ event, variant = 'default' }: SearchHistoryRowProps) => {
  const handleSearch = (rowEvent: RouteSearchUsageEvent) => {
    trackUser.event('Search again from History', {
      origin: rowEvent.data?.origin,
      destination: rowEvent.data?.destination,
      location: variant === 'default' ? 'History Page' : 'App Nav',
    });
  };

  const query = getEventSearchQuery(event);
  if (!query) return null;

  const searchParams = new URLSearchParams(query);
  const advancedSearchLabel = getAdvancedSearchSummaryLabel(JSON.parse(query[QUERY_PARAM.ADVANCED_SEARCH] || '{}'));
  const cargoParams = JSON.parse(query[QUERY_PARAM.CARGO] || '{}');
  const cargoSearchLabel = !isEmpty(cargoParams) ? getCargoOptionSelectionString(cargoParams)?.string : '';
  const locationNames = getEventLocationNames(event);
  const isMiniVariant = variant === 'mini';

  if (!locationNames) return null;

  const { from, to } = locationNames;

  const { eventRow, badge, locationButton, buttonWrapper } = styles({ variant });

  return (
    <div key={event.id} className={eventRow()}>
      {!isMiniVariant && <div>{formatTime(event.createdAt ? new Date(event.createdAt) : new Date())}</div>}
      <div>
        <Link href={`/search?${searchParams}`} type="button" className={locationButton()}>
          {from} to {to}
        </Link>
        {!isMiniVariant && (advancedSearchLabel || cargoSearchLabel) && (
          <div className="flex items-center">
            <Icon name="rules" size="base" color="text.primary" className="mr-sm" />
            {advancedSearchLabel ? <Text className={badge()}>{advancedSearchLabel}</Text> : null}
            {cargoSearchLabel ? (
              <div className="ml-1">
                <Text className={badge()}>{cargoSearchLabel}</Text>
              </div>
            ) : null}
          </div>
        )}
      </div>
      {!isMiniVariant && (
        <div className={buttonWrapper()}>
          <Button
            size="small"
            href={`${ROUTING_SEARCH_URL}?${searchParams}`}
            target="_blank"
            rel="nofollow noopener"
            onClick={() => handleSearch(event)}
          >
            Search again
          </Button>
        </div>
      )}
    </div>
  );
};

const styles = tv({
  slots: {
    eventRow:
      'border-b border-grey-200 px-6 py-4 text-baseSm no-underline last:border-none md:grid md:grid-cols-[100px_1fr_120px] md:items-center',
    locationButton: 'm-0 block truncate font-medium',
    buttonWrapper: 'mt-2 flex items-center justify-end md:mt-0',
    badge: 'm-0 rounded-sm bg-grey-100 p-1 text-sm leading-ultraTight text-text-primary',
  },
  variants: {
    variant: {
      default: {},
      mini: {
        eventRow: 'relative z-2 !border-none px-0 py-0 text-xs md:block',
        locationButton: 'overflow-hidden overflow-ellipsis whitespace-nowrap py-1 font-normal',
      },
    },
  },
});

export { SearchHistoryRow };
